/**
 * Trading hooks — typed TanStack Query wrappers for trading operations.
 *
 * Replaces: js/modules/trading.js (620 lines of vanilla DOM manipulation)
 * Backend:  backend/market/interfaces.py
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiGet, apiPost } from "@/lib/api-client";
import type { ExecutionResult, PortfolioSummary } from "@/sdk/types/market";
import type { Position } from "@/sdk/types/core";

export const TRADING_QUERY_KEYS = {
  botStatus: ["bot", "status"] as const,
  portfolio: ["paper", "portfolio"] as const,
  positions: ["paper", "positions"] as const,
  livePositions: ["live", "positions"] as const,
  liveOpenOrders: ["live", "orders", "open"] as const,
  balance: ["trading", "balance"] as const,
  walletStatus: ["wallet", "status"] as const,
};

export function useBotStatus(refetchInterval = 15_000) {
  return useQuery({
    queryKey: TRADING_QUERY_KEYS.botStatus,
    queryFn: () =>
      apiGet<{
        running: boolean;
        mode: string;
        uptime_seconds: number;
        cycle_count: number;
        markets_scanned: number;
        signals_generated: number;
        last_cycle: string | null;
      }>("/api/bot/status"),
    refetchInterval,
  });
}

export function usePortfolio(refetchInterval = 30_000) {
  return useQuery({
    queryKey: TRADING_QUERY_KEYS.portfolio,
    queryFn: () => apiGet<PortfolioSummary>("/api/paper/portfolio"),
    refetchInterval,
  });
}

export function usePositions(refetchInterval = 30_000) {
  return useQuery({
    queryKey: TRADING_QUERY_KEYS.positions,
    queryFn: () =>
      apiGet<{ positions: Position[] }>("/api/paper/positions"),
    refetchInterval,
  });
}

export interface LiveTradingPosition {
  market_id: string;
  token_id?: string;
  outcome?: string;
  title?: string;
  size: number;
  avg_price?: number;
  current_price?: number;
  unrealized_pnl?: number;
  value?: number;
}

export interface LiveTradingPositionsResponse {
  positions: LiveTradingPosition[];
  source: "clob" | "data-api" | "unavailable" | string;
  note?: string;
}

export interface LiveOpenOrder {
  id?: string;
  order_id?: string;
  orderID?: string;
  hash?: string;
  venue_order_id_redacted?: string;
  market_id?: string;
  token_id?: string;
  side?: "BUY" | "SELL" | string;
  price?: number;
  size?: number;
  status?: string;
  state?: string;
  [key: string]: unknown;
}

export interface LiveOpenOrdersResponse {
  orders: LiveOpenOrder[];
  note?: string;
}

export function useLivePositions(refetchInterval = 30_000) {
  return useQuery({
    queryKey: TRADING_QUERY_KEYS.livePositions,
    queryFn: () =>
      apiGet<LiveTradingPositionsResponse>("/api/trading/positions"),
    refetchInterval,
  });
}

export function useLiveOpenOrders(
  options: { marketId?: string; refetchInterval?: number } = {},
) {
  const queryKey = options.marketId
    ? [...TRADING_QUERY_KEYS.liveOpenOrders, options.marketId] as const
    : TRADING_QUERY_KEYS.liveOpenOrders;
  const query = options.marketId
    ? `?market_id=${encodeURIComponent(options.marketId)}`
    : "";

  return useQuery({
    queryKey,
    queryFn: () =>
      apiGet<LiveOpenOrdersResponse>(`/api/trading/orders/open${query}`),
    refetchInterval: options.refetchInterval ?? 30_000,
  });
}

/**
 * Shape of GET /api/trading/balance. The backend returns ``usdc: 0`` together
 * with ONE of ``error`` / ``_dev_fallback`` / ``trading_enabled: false`` when it
 * could NOT read the real on-chain collateral (credentials not injected, or the
 * cloud IP is geoblocked by Polymarket). That is NOT a real zero balance.
 */
export interface TradingBalanceResponse {
  usdc?: number;
  allowance?: number;
  positions_value?: number;
  open_positions?: number;
  source?: "clob" | "rpc" | "ledger" | "ireland-edge-relay" | "unavailable" | string;
  balance_kind?: "wallet_cash" | "live_position_value" | string;
  wallet_unavailable?: boolean;
  trading_enabled?: boolean;
  error?: string;
  _dev_fallback?: boolean;
  /** ISO timestamp of the read — every backend /balance source emits it. */
  as_of?: string;
  as_of_epoch?: number;
  /**
   * Ireland-edge-relay freshness flag: true once the relayed value has aged
   * past its TTL. The backend still serves the value (it is the only real
   * balance read) — the UI must flag it, never present it as current.
   */
  stale?: boolean;
}

/**
 * Three-state view of the wallet balance. A discriminated union so the UI never
 * confuses a value, a still-loading read, and a failed read — and so TypeScript
 * narrows ``usdc`` to ``number`` under ``status === "available"`` (no casts).
 *
 *  - ``loading``     — read still in flight with no prior value: show a neutral
 *                      placeholder, NEVER a number or "Unavailable".
 *  - ``available``   — a real, successful read: render the number, including a
 *                      genuine ``$0.00`` (truly-empty wallet).
 *  - ``unavailable`` — the read failed; ``reason`` says WHY so the hint is honest:
 *      ``not-configured`` = creds/trading not configured (``_dev_fallback`` /
 *        ``trading_enabled: false``) — a *config* problem, NOT a reachability one;
 *      ``unreachable``    = query error, backend ``error`` payload, or malformed
 *        response — i.e. can't reach Polymarket (geoblock / network).
 */
export type WalletBalanceView =
  | { status: "loading" }
  | {
      status: "available";
      usdc: number;
      label?: "Wallet" | "Ledger";
      hint?: string;
      source?: string;
      /** ISO timestamp of the underlying read, when the backend reported one. */
      asOf?: string;
      /** True ONLY when the backend explicitly flagged the value as aged past TTL. */
      stale?: boolean;
    }
  | { status: "unavailable"; reason: "not-configured" | "unreachable" };

/**
 * Interpret a /api/trading/balance read so a FAILED read is never shown as a
 * real ``$0.00`` AND a still-loading read is never shown as "Unavailable".
 * A genuine empty wallet (successful read, ``usdc: 0``, no failure flags)
 * correctly returns ``available`` with ``usdc: 0``.
 */
export function interpretWalletBalance(
  data: unknown,
  opts?: { isError?: boolean; isLoading?: boolean },
): WalletBalanceView {
  if (opts?.isError) {
    return { status: "unavailable", reason: "unreachable" };
  }
  // Still loading and nothing to show yet → neutral, never "Unavailable".
  // (A background refetch keeps prior data, so we only treat the no-data case
  // as loading; with data present we interpret it.)
  if (opts?.isLoading && data === undefined) {
    return { status: "loading" };
  }
  const rec = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  // Config/credentials missing is a DISTINCT failure mode from unreachable.
  if (rec._dev_fallback === true || rec.trading_enabled === false) {
    return { status: "unavailable", reason: "not-configured" };
  }
  // Freshness pass-through: every backend source emits as_of; only an explicit
  // stale === true (the edge relay's past-TTL annotation) sets the flag — the
  // UI must never present an hours-old relayed value as a current read.
  const freshness: { asOf?: string; stale?: boolean } = {};
  if (typeof rec.as_of === "string") freshness.asOf = rec.as_of;
  if (rec.stale === true) freshness.stale = true;
  if (
    rec.source === "ledger" &&
    rec.balance_kind === "live_position_value" &&
    typeof rec.positions_value === "number" &&
    Number.isFinite(rec.positions_value)
  ) {
    return {
      status: "available",
      usdc: rec.positions_value,
      label: "Ledger",
      hint: "Live position value",
      source: "ledger",
      ...freshness,
    };
  }
  const usdc = rec.usdc;
  if (Boolean(rec.error) || typeof usdc !== "number" || !Number.isFinite(usdc)) {
    return { status: "unavailable", reason: "unreachable" };
  }
  if (rec.source === "ireland-edge-relay") {
    // Keep relay attribution — a frozen relay must stay traceable in the UI.
    return {
      status: "available",
      usdc,
      hint: "Relayed from Ireland edge",
      source: "ireland-edge-relay",
      ...freshness,
    };
  }
  return { status: "available", usdc, ...freshness };
}

export function useTradingBalance(refetchInterval = 30_000) {
  return useQuery({
    queryKey: TRADING_QUERY_KEYS.balance,
    queryFn: () =>
      apiGet<TradingBalanceResponse>("/api/trading/balance"),
    refetchInterval,
    retry: false,
  });
}

export function useStartBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mode: "paper" | "live") =>
      apiPost<{ status: string }>(`/api/bot/start?mode=${mode}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRADING_QUERY_KEYS.botStatus,
      });
    },
  });
}

export function useStopBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ status: string }>("/api/bot/stop"),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRADING_QUERY_KEYS.botStatus,
      });
    },
  });
}

export function usePlaceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      token_id: string;
      side: "BUY" | "SELL";
      price: number;
      size: number;
    }) =>
      // Backend: POST /api/trading/order/limit (live_trading router).
      // /api/trading/order (no sub-path) does not exist.
      apiPost<ExecutionResult>("/api/trading/order/limit", params),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRADING_QUERY_KEYS.portfolio,
      });
      void queryClient.invalidateQueries({
        queryKey: TRADING_QUERY_KEYS.positions,
      });
      void queryClient.invalidateQueries({
        queryKey: TRADING_QUERY_KEYS.livePositions,
      });
      void queryClient.invalidateQueries({
        queryKey: TRADING_QUERY_KEYS.liveOpenOrders,
      });
      void queryClient.invalidateQueries({
        queryKey: TRADING_QUERY_KEYS.balance,
      });
    },
  });
}
